/**
 * Menu Guard — substitui o antigo "Banidos".
 *
 * Tela unica:
 *   - 4 cards: Online In-Game, Heartbeat 30s, Total Conhecidos, HWIDs Distintos
 *   - Lista do _39_AntiCheatHeader, online primeiro, busca por hwid/ip/conta/char
 *   - Acoes por linha: detalhe do HWID + ban duplo
 *   - Botao topo: forcar reinicio (global ou per-user)
 *   - Modal de drill-in HWID com: header + accounts (mesmo HWID + mesmo IP) + historico bans
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2, AlertCircle, Shield, RefreshCw, Search, Activity,
  AlertTriangle, Users, Wifi, Hash, LogOut, X, Ban, Info, Hourglass, Settings, Package,
  Gauge, Skull, Swords, Trees, Globe, Zap, ShieldCheck, ShieldOff, MessageSquare, Monitor, Cpu, ChevronDown,
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  getGuardSummary, getHwidDetail,
  guardBan, guardUnban, guardForceRestart,
  listLockouts, cancelLockout,
  getGameVersion, setGameVersion, launcherUpdate,
  getNextGuardSessions, nextGuardCommand,
  type NextGuardState, type NextGuardSession, type NextGuardTarget, type NextGuardCmd,
  getMoveguardStats, listGuardAlerts, classifyWorld,
  type GuardSummary, type HwidDetail, type LockoutRow,
  type MoveguardStats, type GuardAlert, type WorldKind,
} from '@/shared/services/adminService';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useToast } from '@/shared/contexts/ToastContext';
import { worldName } from '@/features/Notifications/worldNames';
import { RowActions } from '@/shared/components/ui/RowActions';

const POLL_MS = 10000;

type LocFilter = WorldKind | 'all';

/* Sessões = o NextGuard (o guard do Cabal Next PC/Android; dono 2026-10-20: a aba "Cabal Next" virou esta).
 * Cada jogo aberto mantém uma conexão de guard: plataforma, HWID, IP, versão e o personagem logado.
 * Ações: forçar reinício (lockout: a API fecha os jogos no NextGuard e recusa quem abrir na janela), mensagem pra
 * todos/um, fechar um jogo, versão do jogo, atualizar launcher, HWID e banir. */
const EVENT_LABEL: Record<string, string> = {
  opened: 'abriu', resumed: 'retomou', lost: 'caiu', ended: 'encerrou', refused: 'recusado', changed: 'personagem',
  integrity: 'integridade',
};
const EVENT_COLOR: Record<string, string> = {
  opened: 'text-emerald-400', resumed: 'text-emerald-300', lost: 'text-amber-400', ended: 'text-white/55',
  refused: 'text-red-400', changed: 'text-[#00e1d7]', integrity: 'text-orange-400',
};

function fmtDuration(sec: number) {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return h > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${m}min`;
}

type Pending = { target: NextGuardTarget; cmd: NextGuardCmd; session?: NextGuardSession };

function GuardSessionsTab({ initialLoc = 'all' }: { initialLoc?: LocFilter }) {
  const { hasFeature } = useAuth();
  const { notify } = useToast();
  const canBan = hasFeature('ban');
  // Filtro por localização: campo / dungeon / guerra (World 15). Classificação
  // client-side pelo WorldIdx do char — ver classifyWorld no adminService.
  const [locFilter, setLocFilter] = useState<LocFilter>(initialLoc);

  const [state, setState] = useState<NextGuardState | null>(null);
  const [summary, setSummary] = useState<GuardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [showEvents, setShowEvents] = useState(false);

  // Lockouts em progresso (mostrados aqui — saíram da tela de Onlines)
  const [activeLockouts, setActiveLockouts] = useState<LockoutRow[]>([]);

  // Modais
  const [hwidOpen, setHwidOpen] = useState<string | null>(null);
  const [banOpen, setBanOpen] = useState<{ userNum: number; accountId?: string | null } | null>(null);
  const [restartOpen, setRestartOpen] = useState(false);
  const [gameVerOpen, setGameVerOpen] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [ng, sum, lockouts] = await Promise.all([
        getNextGuardSessions(),
        getGuardSummary(),
        listLockouts().catch(() => []),
      ]);
      setState(ng);
      setSummary(sum);
      setActiveLockouts(lockouts);
      setError(null);
      setLastUpdate(new Date());
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Erro ao carregar Guard');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const onCancelLockout = async (id: number) => {
    try {
      await cancelLockout(id);
      notify('success', 'Lockout cancelado');
      fetchData(true);
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    }
  };

  const items = state?.sessions ?? [];
  const pc = items.filter((s) => s.platform === 'pc').length;
  const android = items.filter((s) => s.platform === 'android').length;
  const inWorld = items.filter((s) => s.inWorld).length;

  // Localização por linha (null = sem char/mapa → só aparece em "Todos").
  const locOf = (r: NextGuardSession): WorldKind | null =>
    r.charIdx > 0 && r.worldIdx != null ? classifyWorld(r.worldIdx) : null;

  // Contagem por localização (pros contadores dos botões de filtro).
  const locCounts = useMemo(() => {
    const c = { all: items.length, campo: 0, dungeon: 0, guerra: 0 };
    for (const r of items) { const k = locOf(r); if (k) c[k]++; }
    return c;
  }, [items]);

  // Busca client-side (as sessões vivem em memória na API) + filtro de localização.
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return items.filter((r) => {
      if (locFilter !== 'all' && locOf(r) !== locFilter) return false;
      if (!s) return true;
      return [r.charName, r.hwid, r.ip, r.device, String(r.userNum), String(r.charIdx)]
        .some((v) => v && v.toLowerCase().includes(s));
    });
  }, [items, search, locFilter]);

  useEffect(() => {
    fetchData();
    const id = setInterval(() => fetchData(true), POLL_MS);
    return () => clearInterval(id);
  }, [fetchData]);

  return (
    <div>
      {/* Sub-header — status do NextGuard + as actions (titulo Guard fica no wrapper) */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="text-xs text-white/60 flex items-center gap-1.5 flex-wrap">
          {state?.serviceUp
            ? <><ShieldCheck size={14} className="text-emerald-400" /><span className="text-emerald-400 font-semibold">NextGuard no ar</span></>
            : <><ShieldOff size={14} className="text-red-400" /><span className="text-red-400 font-semibold">NextGuard sem resposta</span></>}
          <span>· {items.length.toLocaleString('pt-BR')} sessões ({pc} PC, {android} Android) · {inWorld} no mundo</span>
          {lastUpdate && <span>· atualizado {lastUpdate.toLocaleTimeString('pt-BR')}</span>}
        </p>
        <div className="flex gap-2 flex-wrap">
          {canBan && (
            <button
              onClick={() => setRestartOpen(true)}
              title="Fecha os jogos conectados e recusa quem abrir durante a janela"
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold
                         bg-red-500/10 border border-red-500/30 text-red-400
                         hover:bg-red-500/20 transition cursor-pointer"
            >
              <LogOut size={13} /> Forçar reinício
            </button>
          )}
          {canBan && (
            <button
              onClick={() => setPending({ target: 'all', cmd: 'message' })}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold
                         bg-white/[0.04] border border-white/[0.08] text-white/75
                         hover:bg-white/[0.08] transition cursor-pointer"
            >
              <MessageSquare size={13} /> Mensagem a todos
            </button>
          )}
          {canBan && (
            <button
              onClick={() => setGameVerOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold
                         bg-purple-500/10 border border-purple-500/30 text-purple-400
                         hover:bg-purple-500/20 transition cursor-pointer"
            >
              <Settings size={13} /> Versão do jogo
            </button>
          )}
          {canBan && (
            <button
              onClick={async () => {
                try { await launcherUpdate(); notify('success', 'Launcher atualizado — manifests regerados.'); }
                catch (e: any) { notify('error', e?.detail || e?.message || 'Falha ao atualizar o launcher.'); }
              }}
              title="Roda o update_all.sh (regenera os manifests do launcher)"
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold
                         bg-amber-500/10 border border-amber-500/30 text-amber-400
                         hover:bg-amber-500/20 transition cursor-pointer"
            >
              <Package size={13} /> Atualizar Launcher
            </button>
          )}
          <button
            onClick={() => fetchData()}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold
                       bg-[#00e1d7]/10 border border-[#00e1d7]/25 text-[#00e1d7]
                       hover:bg-[#00e1d7]/20 transition cursor-pointer disabled:opacity-50"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Atualizar
          </button>
        </div>
      </div>

      {/* Lockouts em progresso — dá pra cancelar antes do BlockUntil */}
      {activeLockouts.length > 0 && (
        <div className="surface-card rounded-xl p-4 mb-4 border-orange-500/30 bg-orange-500/[0.04]">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={16} className="text-orange-400" />
            <span className="text-xs font-black uppercase tracking-[0.15em] text-orange-400">
              {activeLockouts.length} lockout{activeLockouts.length > 1 ? 's' : ''} ativo{activeLockouts.length > 1 ? 's' : ''}
            </span>
          </div>
          <div className="space-y-1.5">
            {activeLockouts.map(lo => (
              <div key={lo.id} className="flex items-center gap-3 text-[11px]">
                <span className={`px-2 py-0.5 rounded font-bold ${lo.targetUserNum ? 'bg-blue-500/15 text-blue-400' : 'bg-red-500/15 text-red-400'}`}>
                  {lo.targetUserNum ? `User ${lo.targetUserNum}` : 'GLOBAL'}
                </span>
                <span className="text-white/75 flex-1">{lo.reason}</span>
                <span className="text-white/45 font-mono">
                  até {new Date(lo.blockUntil).toLocaleTimeString('pt-BR')}
                </span>
                {canBan && (
                  <button
                    onClick={() => onCancelLockout(lo.id)}
                    className="text-red-400/70 hover:text-red-400 cursor-pointer"
                    title="Cancelar"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cards — Online no Guard em destaque (mesma fonte da pagina /admin/onlines) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <SummaryCard
          color="#10b981" icon={<Wifi size={18} />}
          label="Online no Guard" value={summary?.onlineGuard ?? items.filter((s) => s.online).length}
          sub={`NextGuard · ${(summary?.onlineInGame ?? 0).toLocaleString('pt-BR')} online in-game`}
        />
        <SummaryCard
          color="#fa3a3a" icon={<Ban size={18} />}
          label="Total Banidos" value={summary?.totalBans ?? 0}
          sub="bans ativos"
        />
        <SummaryCard
          color="#f59e0b" icon={<Activity size={18} />}
          label="Banimentos Hoje" value={summary?.bansToday ?? 0}
          sub="banidos nas últimas 24h"
        />
        <SummaryCard
          color="#a855f7" icon={<Hash size={18} />}
          label="Total HWID" value={summary?.distinctHwids ?? 0}
          sub="máquinas únicas"
        />
      </div>

      {/* Filtro por localização — campo / dungeon / guerra (classificado pelo mapId) */}
      <div className="flex flex-wrap gap-2 mb-3">
        <LocBtn active={locFilter === 'all'}     onClick={() => setLocFilter('all')}
                icon={<Globe size={13} />} label="Todos"   count={locCounts.all} />
        <LocBtn active={locFilter === 'campo'}   onClick={() => setLocFilter('campo')}
                icon={<Trees size={13} />} label="Campo"   count={locCounts.campo} color="#22c55e" />
        <LocBtn active={locFilter === 'dungeon'} onClick={() => setLocFilter('dungeon')}
                icon={<Skull size={13} />} label="Dungeon" count={locCounts.dungeon} color="#f59e0b" />
        <LocBtn active={locFilter === 'guerra'}  onClick={() => setLocFilter('guerra')}
                icon={<Swords size={13} />} label="Guerra"  count={locCounts.guerra} color="#fa3a3a" />
      </div>

      {/* Search */}
      <div className="surface-card rounded-xl p-3 mb-4 flex items-center gap-3">
        <Search size={16} className="text-white/45 ml-1" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por personagem, conta, HWID, IP ou aparelho..."
          className="flex-1 bg-transparent text-sm text-white placeholder:text-white/35 outline-none"
        />
        {search && (
          <button onClick={() => setSearch('')} className="text-white/45 hover:text-white">
            <X size={14} />
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="surface-card rounded-xl p-4 mb-4 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Lista — sessões do NextGuard (segurança + char) */}
      <div className="surface-card rounded-xl overflow-hidden mb-5">
        {loading && !state ? (
          <div className="py-16 flex justify-center text-white/40"><Loader2 size={20} className="animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-white/40 text-sm">
            {search || locFilter !== 'all' ? 'Nenhum resultado' : 'Nenhum jogo conectado ao NextGuard'}
          </div>
        ) : (
          <div className="max-h-[64vh] overflow-y-auto">
            {filtered.map((s) => (
              <div key={s.token}
                className="px-4 py-3 flex items-center gap-3 border-b border-white/[0.04] last:border-0 hover:bg-white/[0.02]">
                <span className="shrink-0 text-white/60" title={s.platform === 'android' ? 'Android' : 'PC'}>
                  {s.platform === 'android' ? <Smartphone size={16} /> : <Monitor size={16} />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {s.charIdx > 0 ? (
                      <Link to={`/admin/character/${s.charIdx}`} className="text-sm font-bold text-white hover:text-[#00e1d7] truncate">
                        {s.charName || `#${s.charIdx}`}
                      </Link>
                    ) : (
                      <span className="text-sm font-bold text-white/50">sem personagem</span>
                    )}
                    {s.userNum > 0 && (
                      <Link to={`/admin/account/${s.userNum}`} className="text-[11px] text-white/45 font-mono hover:text-[#00e1d7]">
                        conta {s.userNum}
                      </Link>
                    )}
                    {s.inWorld && (s.channel != null || s.worldIdx != null) && (
                      <span className="text-[11px] text-white/55">
                        {s.channel != null ? `canal ${s.channel}` : ''}
                        {s.channel != null && s.worldIdx != null ? ' · ' : ''}
                        {s.worldIdx != null ? worldName(s.worldIdx) : ''}
                      </span>
                    )}
                    {!s.online && (
                      <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30">
                        RECONECTANDO
                      </span>
                    )}
                    {s.banned && (
                      <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 border border-red-500/30">
                        HWID BANIDO
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-white/50 font-mono truncate">
                    {s.device || '—'} · {s.version || `build ${s.build}`} · {s.ip || '—'}
                    {s.resumes > 0 ? ` · ${s.resumes} reconexões` : ''}
                  </div>
                </div>
                <button
                  onClick={() => s.hwid && setHwidOpen(s.hwid)}
                  title={`Detalhes do HWID ${s.hwid}`}
                  className="hidden md:flex items-center gap-1.5 text-white/40 hover:text-[#00e1d7] shrink-0 cursor-pointer"
                >
                  <Cpu size={12} />
                  <span className="text-[10px] font-mono">{s.hwid.length > 14 ? s.hwid.slice(0, 14) + '…' : s.hwid}</span>
                </button>
                <span className="text-[11px] text-white/45 font-mono whitespace-nowrap shrink-0 hidden sm:block">
                  {fmtDuration(s.onlineSec)}
                </span>
                <RowActions
                  actions={[
                    ...(canBan ? [
                      { label: 'Mandar mensagem', icon: <MessageSquare size={13} />,
                        onClick: () => setPending({ target: 'token', cmd: 'message', session: s }),
                        variant: 'primary' as const },
                      { label: 'Fechar o jogo', icon: <LogOut size={13} />,
                        onClick: () => setPending({ target: 'token', cmd: 'close', session: s }),
                        variant: 'danger' as const },
                    ] : []),
                    { label: 'Detalhes do HWID', icon: <Info size={13} />,
                      onClick: () => s.hwid && setHwidOpen(s.hwid),
                      variant: 'primary' as const, disabled: !s.hwid },
                    ...(canBan && s.userNum > 0 ? [{
                      label: 'Banir conta + HWID', icon: <Ban size={13} />,
                      onClick: () => setBanOpen({ userNum: s.userNum }),
                      variant: 'danger' as const,
                    }] : []),
                  ]}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <button
        onClick={() => setShowEvents((v) => !v)}
        className="flex items-center gap-2 text-sm font-semibold text-white/70 hover:text-white mb-3 cursor-pointer"
      >
        <ChevronDown size={15} className={`transition-transform ${showEvents ? 'rotate-180' : ''}`} />
        Eventos recentes ({state?.events.length ?? 0})
      </button>
      {showEvents && (
        <div className="surface-card rounded-xl overflow-hidden">
          <div className="max-h-[50vh] overflow-y-auto font-mono text-[11px]">
            {(state?.events ?? []).map((e, i) => (
              <div key={i} className="px-4 py-1.5 flex gap-3 border-b border-white/[0.04] last:border-0">
                <span className="text-white/40 whitespace-nowrap">{new Date(e.ts).toLocaleTimeString('pt-BR')}</span>
                <span className={`w-20 shrink-0 ${EVENT_COLOR[e.event] || 'text-white/60'}`}>
                  {EVENT_LABEL[e.event] || e.event}
                </span>
                <span className="text-white/60 shrink-0">{e.platform}</span>
                <span className="text-white/75 truncate">
                  {e.charIdx > 0 ? `char ${e.charIdx} · ` : ''}{e.hwid || '—'} · {e.ip || '—'}
                  {e.text ? ` · ${e.text}` : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modais */}
      {hwidOpen && (
        <HwidDetailModal
          hwid={hwidOpen}
          canBan={canBan}
          onClose={() => setHwidOpen(null)}
          onBan={(userNum, accountId) => {
            setHwidOpen(null);
            setBanOpen({ userNum, accountId });
          }}
          onChanged={() => fetchData(true)}
        />
      )}
      {banOpen && (
        <BanModal
          target={banOpen}
          onClose={() => setBanOpen(null)}
          onDone={() => { setBanOpen(null); fetchData(); }}
        />
      )}
      {restartOpen && (
        <ForceRestartModal
          onClose={() => setRestartOpen(false)}
          onDone={() => { setRestartOpen(false); fetchData(); }}
        />
      )}
      {gameVerOpen && (
        <GameVersionModal onClose={() => setGameVerOpen(false)} />
      )}
      {pending && (
        <CommandModal
          pending={pending}
          onClose={() => setPending(null)}
          onDone={() => { setPending(null); setTimeout(() => fetchData(true), 1500); }}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────── Mensagem / fechar (NextGuard) ────
 * Mensagem pra todos ou pra um jogo, fechar um jogo. "Fechar todos" = Forçar
 * reinício (lockout), que também segura quem tentar abrir durante a janela. */

function CommandModal({ pending, onClose, onDone }:
  { pending: Pending; onClose: () => void; onDone: () => void }) {
  const { notify } = useToast();
  const [text, setText] = useState(pending.cmd === 'close' ? 'O jogo foi fechado pela equipe.' : '');
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await nextGuardCommand({
        target: pending.target, cmd: pending.cmd, token: pending.session?.token, text: text.trim(),
      });
      notify('success', pending.cmd === 'close' ? 'Comando de fechar enviado' : 'Mensagem enviada');
      onDone();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal onClose={onClose} maxW="440px">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-bold text-white">
          {pending.cmd === 'close' ? 'Fechar o jogo' : 'Mandar mensagem'}
          <span className="text-white/50 font-normal">
            {' — '}{pending.target === 'all'
              ? 'todos os jogos conectados'
              : (pending.session?.charName || pending.session?.hwid || 'sessão')}
          </span>
        </h3>
        <button onClick={onClose} className="text-white/50 hover:text-white cursor-pointer"><X size={18} /></button>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, 250))}
        rows={3}
        placeholder="Texto que aparece no jogo"
        className="w-full bg-[#161e26] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white
                   placeholder:text-white/40 focus:outline-none focus:border-[#00e1d7]/40 transition mb-4"
      />
      <div className="flex justify-end gap-2">
        <button onClick={onClose}
          className="px-3 py-2 rounded-lg text-sm text-white/70 hover:bg-white/[0.06] cursor-pointer">Cancelar</button>
        <button
          onClick={send}
          disabled={sending || !text.trim()}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition cursor-pointer disabled:opacity-50 ${
            pending.cmd === 'close'
              ? 'bg-red-500/15 border border-red-500/30 text-red-400 hover:bg-red-500/25'
              : 'bg-[#00e1d7]/10 border border-[#00e1d7]/25 text-[#00e1d7] hover:bg-[#00e1d7]/20'
          }`}
        >
          {sending && <Loader2 size={14} className="animate-spin" />}
          {pending.cmd === 'close' ? 'Fechar' : 'Enviar'}
        </button>
      </div>
    </Modal>
  );
}

/* ───────────────────────────────────────────────────── Components ──── */

function SummaryCard({ color, icon, label, value, sub }:
  { color: string; icon: React.ReactNode; label: string; value: number; sub: string }) {
  return (
    <div className="surface-card rounded-xl p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] font-bold text-white/65 uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-black text-white mt-1 leading-none">
            {value.toLocaleString('pt-BR')}
          </p>
          <p className="text-[10px] mt-1.5 font-medium text-white/45">{sub}</p>
        </div>
        <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border"
             style={{ background: `${color}15`, borderColor: `${color}30`, color }}>
          {icon}
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────────────────────────────────── HWID modal ──── */

function HwidDetailModal({ hwid, canBan, onClose, onBan, onChanged }: {
  hwid: string;
  canBan: boolean;
  onClose: () => void;
  onBan: (userNum: number, accountId?: string | null) => void;
  onChanged: () => void;
}) {
  const [data, setData] = useState<HwidDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { notify } = useToast();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await getHwidDetail(hwid);
        if (alive) setData(d);
      } catch (err: any) {
        if (alive) setError(err?.detail || err?.message || 'Erro');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [hwid]);

  const onUnban = async (userNum: number) => {
    try {
      await guardUnban(userNum);
      notify('success', 'Desbanido');
      const d = await getHwidDetail(hwid);
      setData(d);
      onChanged();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    }
  };

  return (
    <Modal onClose={onClose} maxW="900px">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Hash size={18} className="text-amber-400" />
            HWID
          </h2>
          <p className="text-[11px] font-mono text-white/55 mt-1 break-all">{hwid}</p>
        </div>
        <button onClick={onClose} className="text-white/45 hover:text-white">
          <X size={18} />
        </button>
      </div>

      {loading ? (
        <div className="py-12 flex items-center justify-center text-white/40">
          <Loader2 size={20} className="animate-spin" />
        </div>
      ) : error ? (
        <div className="text-sm text-red-400 py-6 text-center">{error}</div>
      ) : data ? (
        <div className="space-y-5">
          {/* Header agregado */}
          <div className="grid grid-cols-3 gap-3 text-xs">
            <Field label="Último IP" value={data.lastSeenIp || '—'} mono />
            <Field label="Último Login"
                   value={data.lastLogin ? new Date(data.lastLogin).toLocaleString('pt-BR') : '—'} />
            <Field label="Último Heartbeat"
                   value={data.lastHeartbeatAt ? new Date(data.lastHeartbeatAt).toLocaleString('pt-BR') : '—'} />
          </div>

          {/* Contas mesmo HWID */}
          <Section title={`Contas neste HWID (${data.accountsSameHwid.length})`}>
            <AccountList items={data.accountsSameHwid} canBan={canBan}
                         onBan={onBan} onUnban={onUnban} />
          </Section>

          {/* Contas mesmo IP (HWID diferente) */}
          {data.accountsSameIp.length > 0 && (
            <Section title={`Contas no mesmo IP — HWID diferente (${data.accountsSameIp.length})`}>
              <AccountList items={data.accountsSameIp} canBan={canBan}
                           onBan={onBan} onUnban={onUnban} />
            </Section>
          )}

          {/* Historico bans */}
          <Section title={`Histórico de bans (${data.bansHistory.length})`}>
            {data.bansHistory.length === 0 ? (
              <div className="text-xs text-white/40 py-3 text-center">Sem registros</div>
            ) : (
              <div className="space-y-2">
                {data.bansHistory.map((b) => (
                  <div key={b.banId}
                       className={`text-xs rounded-lg p-3 border ${
                         b.isActive
                           ? 'bg-red-500/[0.06] border-red-500/30'
                           : 'bg-white/[0.02] border-white/[0.05] opacity-60'
                       }`}>
                    <div className="flex items-center justify-between mb-1">
                      <Link to={`/admin/account/${b.userNum}`}
                            className="text-[#00e1d7] font-bold hover:underline">
                        UserNum {b.userNum}
                      </Link>
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-black ${
                        b.isActive ? 'bg-red-500/20 text-red-400' : 'bg-white/[0.05] text-white/45'
                      }`}>
                        {b.isActive ? 'ATIVO' : 'EXPIRADO'}
                      </span>
                    </div>
                    <p className="text-white/75 leading-relaxed">{b.motivo}</p>
                    <p className="text-[10px] text-white/40 mt-1 font-mono">
                      banido em {new Date(b.dataBanimento).toLocaleString('pt-BR')}
                      {b.dataLiberacao && (
                        <> · libera em {new Date(b.dataLiberacao).toLocaleString('pt-BR')}</>
                      )}
                      {!b.dataLiberacao && b.isActive && <> · permanente</>}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      ) : null}
    </Modal>
  );
}

function AccountList({ items, canBan, onBan, onUnban }: {
  items: { userNum: number; accountId?: string | null; ip?: string | null;
           lastLogin?: string | null; isOnline: boolean; charName?: string | null;
           activeBans: number }[];
  canBan: boolean;
  onBan: (userNum: number, accountId?: string | null) => void;
  onUnban: (userNum: number) => void;
}) {
  if (items.length === 0) {
    return <div className="text-xs text-white/40 py-3 text-center">Nenhuma conta</div>;
  }
  return (
    <div className="space-y-1.5">
      {items.map((a) => (
        <div key={a.userNum}
             className="flex items-center gap-3 text-xs rounded-lg px-3 py-2
                        bg-white/[0.02] border border-white/[0.04]">
          <span className={`w-2 h-2 rounded-full shrink-0 ${
            a.isOnline ? 'bg-emerald-400' : 'bg-white/15'
          }`} />
          <Link to={`/admin/account/${a.userNum}`}
                className="text-[#00e1d7] font-bold hover:underline min-w-[120px]">
            {a.accountId || `#${a.userNum}`}
          </Link>
          {a.isOnline && a.charName && (
            <span className="text-white/75 truncate">{a.charName}</span>
          )}
          <span className="text-white/45 font-mono ml-auto">{a.ip || '—'}</span>
          <span className="text-white/35 text-[10px]">
            {a.lastLogin ? new Date(a.lastLogin).toLocaleDateString('pt-BR') : '—'}
          </span>
          {a.activeBans > 0 && (
            <span className="px-1.5 py-0.5 rounded text-[9px] font-black bg-red-500/15
                             text-red-400 border border-red-500/30">
              {a.activeBans} BAN
            </span>
          )}
          {canBan && (
            <div className="flex gap-1">
              {a.activeBans > 0 ? (
                <button onClick={() => onUnban(a.userNum)}
                        className="px-2 py-1 rounded text-[10px] font-bold
                                   bg-emerald-500/10 text-emerald-400 border border-emerald-500/30
                                   hover:bg-emerald-500/20 cursor-pointer">
                  Desban
                </button>
              ) : (
                <button onClick={() => onBan(a.userNum, a.accountId)}
                        className="px-2 py-1 rounded text-[10px] font-bold
                                   bg-red-500/10 text-red-400 border border-red-500/30
                                   hover:bg-red-500/20 cursor-pointer">
                  Banir
                </button>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ───────────────────────────────────────────────────── Ban modal ──── */

function BanModal({ target, onClose, onDone }: {
  target: { userNum: number; accountId?: string | null };
  onClose: () => void;
  onDone: () => void;
}) {
  const [days, setDays] = useState(7);
  const [motivo, setMotivo] = useState('');
  const [busy, setBusy] = useState(false);
  const { notify } = useToast();

  const submit = async () => {
    if (motivo.trim().length < 2) {
      notify('error', 'Motivo obrigatório');
      return;
    }
    setBusy(true);
    try {
      await guardBan({ userNum: target.userNum, days, motivo: motivo.trim() });
      notify('success', `Banido por ${days} dias`);
      onDone();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} maxW="500px">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <Ban size={18} className="text-red-400" />
          Banir conta + HWID
        </h2>
        <button onClick={onClose} className="text-white/45 hover:text-white">
          <X size={18} />
        </button>
      </div>

      <div className="space-y-4">
        <div className="rounded-lg bg-white/[0.03] border border-white/[0.06] p-3 text-xs">
          <p className="text-white/55">Conta-alvo:</p>
          <p className="text-white font-bold mt-1">
            {target.accountId || `UserNum ${target.userNum}`}
            <span className="text-white/40 font-normal ml-2">#{target.userNum}</span>
          </p>
        </div>

        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-white/55 block mb-1.5">
            Duração (dias)
          </label>
          <input type="number" value={days} min={1} max={3650}
                 onChange={(e) => setDays(Math.max(1, Math.min(3650, +e.target.value || 1)))}
                 className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08]
                            text-sm text-white outline-none focus:border-[#00e1d7]/40" />
        </div>

        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-white/55 block mb-1.5">
            Motivo
          </label>
          <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)}
                    rows={3} maxLength={512}
                    placeholder="Descreva o motivo do banimento..."
                    className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08]
                               text-sm text-white outline-none resize-none
                               focus:border-[#00e1d7]/40" />
        </div>

        <div className="rounded-lg bg-amber-500/[0.05] border border-amber-500/20 p-3 flex items-start gap-2">
          <AlertTriangle size={14} className="text-amber-400 shrink-0 mt-0.5" />
          <p className="text-[11px] text-white/65 leading-relaxed">
            Banimento <b>duplo</b>: trava no login (Cabal nativo) E registra em
            <code className="text-white/85 mx-1">_39_AntiCheatBans</code>.
            Via match transitivo por HWID, todas as contas na mesma máquina serão bloqueadas pela DLL.
          </p>
        </div>

        <div className="flex gap-2 pt-2">
          <button onClick={onClose} disabled={busy}
                  className="flex-1 px-3 py-2 rounded-lg text-sm font-semibold
                             bg-white/[0.04] border border-white/[0.08] text-white/75
                             hover:bg-white/[0.06] transition cursor-pointer">
            Cancelar
          </button>
          <button onClick={submit} disabled={busy || motivo.trim().length < 2}
                  className="flex-1 px-3 py-2 rounded-lg text-sm font-semibold
                             bg-red-500/15 border border-red-500/40 text-red-300
                             hover:bg-red-500/25 transition cursor-pointer disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin inline" /> : 'Confirmar ban'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ──────────────────────────────────────────────── Force restart modal ────
 * Layout portado do KickModal da tela de Onlines (seletor Global/Conta, mais
 * bonito) — mas chamando /admin/guard/force-restart, que cria o lockout E faz
 * o push instantâneo (WS pra DLL + ZMQ pras .so). As ações saíram da tela de
 * Onlines: agora só existem aqui. */

function ForceRestartModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { notify } = useToast();
  const [duration, setDuration] = useState(30);
  const [reason, setReason] = useState('Atualização em andamento. Reinicie o cliente.');
  const [target, setTarget] = useState<'global' | 'user'>('global');
  const [targetUser, setTargetUser] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (target === 'user' && !targetUser.trim()) {
      notify('error', 'Informe o UserNum'); return;
    }
    setBusy(true);
    try {
      await guardForceRestart({
        targetUserNum: target === 'user' ? parseInt(targetUser, 10) : null,
        durationSeconds: duration,
        reason: reason || 'Reinicie o cliente.',
      });
      notify('success', target === 'global' ? 'Lockout GLOBAL ativo' : `Lockout pro user ${targetUser}`);
      onDone();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} maxW="440px">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <LogOut size={18} className="text-red-400" />
          Forçar reinício
        </h3>
        <button onClick={onClose} className="text-white/55 hover:text-white cursor-pointer">
          <X size={18} />
        </button>
      </div>

      <p className="text-[11px] text-white/55 mb-4 leading-relaxed">
        Cria janela de bloqueio: todo jogo conectado no NextGuard (inclusive quem
        ainda está na tela de login) recebe a mensagem e fecha. Quem abrir o jogo
        durante a janela também é fechado com a mesma mensagem.
      </p>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <button
          type="button" onClick={() => setTarget('global')}
          className={`px-3 py-2.5 rounded-lg text-sm font-bold cursor-pointer transition
                      ${target === 'global'
                        ? 'bg-red-500/15 text-red-400 border border-red-400/40'
                        : 'bg-white/[0.04] text-white/70 border border-white/[0.06]'}`}
        >
          Todos (global)
        </button>
        <button
          type="button" onClick={() => setTarget('user')}
          className={`px-3 py-2.5 rounded-lg text-sm font-bold cursor-pointer transition
                      ${target === 'user'
                        ? 'bg-blue-500/15 text-blue-400 border border-blue-400/40'
                        : 'bg-white/[0.04] text-white/70 border border-white/[0.06]'}`}
        >
          Conta específica
        </button>
      </div>

      {target === 'user' && (
        <div className="mb-3">
          <label className="text-[11px] text-white/55 mb-1 block">UserNum alvo</label>
          <input
            type="text" inputMode="numeric"
            value={targetUser}
            onChange={(e) => setTargetUser(e.target.value.replace(/\D/g, ''))}
            className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg
                       px-3 py-2 text-sm text-white font-mono"
          />
        </div>
      )}

      <div className="mb-3">
        <label className="text-[11px] text-white/55 mb-1 block">
          Duração (segundos) — entre 5 e 3600
        </label>
        <div className="flex items-center gap-2">
          <input
            type="number" min={5} max={3600}
            value={duration}
            onChange={(e) => setDuration(parseInt(e.target.value || '30', 10))}
            className="flex-1 bg-[#0d141b] border border-white/[0.06] rounded-lg
                       px-3 py-2 text-sm text-white font-mono"
          />
          <Hourglass size={14} className="text-white/45" />
        </div>
      </div>

      <div className="mb-5">
        <label className="text-[11px] text-white/55 mb-1 block">Mensagem ao cliente</label>
        <input
          type="text" value={reason} maxLength={255}
          onChange={(e) => setReason(e.target.value)}
          className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg
                     px-3 py-2 text-sm text-white"
        />
      </div>

      <div className="flex gap-2">
        <button
          onClick={onClose}
          className="flex-1 px-4 py-2.5 rounded-lg bg-white/[0.04] text-white/75
                     hover:bg-white/[0.08] transition cursor-pointer text-sm"
        >
          Cancelar
        </button>
        <button
          onClick={submit}
          disabled={busy}
          className="flex-1 px-4 py-2.5 rounded-lg bg-red-500 hover:bg-red-400
                     text-white font-bold text-sm transition cursor-pointer
                     disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {busy && <Loader2 size={14} className="animate-spin" />}
          Aplicar
        </button>
      </div>
    </Modal>
  );
}

/* ──────────────────────────────────────────────── Versão do jogo modal ────
 * Cabal Next: o número da tela de título ("Ver. 1.87" = 187). O client manda no
 * Hello do NextGuard; quem ABRE o jogo com versão menor é recusado (mensagem de
 * atualização + fecha). Quem já está jogando não cai. Só a compilação release
 * (a DEV não é checada); 0 = desligada. */

function GameVersionModal({ onClose }: { onClose: () => void }) {
  const { notify } = useToast();
  const [current, setCurrent] = useState<number | null>(null);
  const [input, setInput] = useState('');
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await getGameVersion();
        if (!alive) return;
        setCurrent(d.version);
        setInput(String(d.version || 0));
        setUpdatedAt(d.updatedAt ?? null);
      } catch (err: any) {
        if (alive) notify('error', err?.detail || err?.message || 'Erro ao carregar versão');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [notify]);

  const submit = async () => {
    const v = parseInt(input.trim(), 10);
    if (Number.isNaN(v) || v < 0) { notify('error', 'Versão inválida'); return; }
    setBusy(true);
    try {
      await setGameVersion(v);
      notify('success', v > 0 ? `Versão do jogo configurada: ${v}` : 'Checagem de versão desligada');
      onClose();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} maxW="440px">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <Settings size={18} className="text-purple-400" />
          Versão do jogo
        </h3>
        <button onClick={onClose} className="text-white/55 hover:text-white cursor-pointer">
          <X size={18} />
        </button>
      </div>

      <p className="text-[11px] text-white/55 mb-4 leading-relaxed">
        É o número da <b>tela de início</b> do jogo (<b>Ver. 1.87</b> = <b>187</b>). Ao abrir, o jogo manda a
        versão pro NextGuard: se for <b>menor</b> que a configurada aqui, o jogador recebe a mensagem de
        <b> versão desatualizada</b> e o jogo fecha (PC e Android). Quem já está jogando não cai. Só a
        compilação <b>release</b> é checada — a DEV não. <b>0</b> desliga a checagem.
      </p>

      {loading ? (
        <div className="py-6 flex items-center justify-center text-white/40">
          <Loader2 size={18} className="animate-spin" />
        </div>
      ) : (
        <>
          <div className="rounded-lg bg-white/[0.03] border border-white/[0.06] p-3 text-xs mb-4">
            <p className="text-white/55">Versão atual configurada:</p>
            <p className="text-white font-bold text-lg mt-0.5">
              {current && current > 0 ? current : '— (check desativado)'}
            </p>
            {updatedAt && (
              <p className="text-[10px] text-white/40 font-mono mt-1">
                alterada em {new Date(updatedAt).toLocaleString('pt-BR')}
              </p>
            )}
          </div>

          <div className="mb-5">
            <label className="text-[11px] text-white/55 mb-1 block">Nova versão</label>
            <input
              type="text" inputMode="numeric"
              value={input}
              onChange={(e) => setInput(e.target.value.replace(/\D/g, ''))}
              className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg
                         px-3 py-2 text-sm text-white font-mono"
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-lg bg-white/[0.04] text-white/75
                         hover:bg-white/[0.08] transition cursor-pointer text-sm"
            >
              Cancelar
            </button>
            <button
              onClick={submit}
              disabled={busy}
              className="flex-1 px-4 py-2.5 rounded-lg bg-purple-500 hover:bg-purple-400
                         text-white font-bold text-sm transition cursor-pointer
                         disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {busy && <Loader2 size={14} className="animate-spin" />}
              Salvar
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

/* ───────────────────────────────────────────────────── Generic UI ──── */

function Modal({ children, onClose, maxW = '600px' }:
  { children: React.ReactNode; onClose: () => void; maxW?: string }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div onClick={(e) => e.stopPropagation()}
           className="surface-card rounded-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
           style={{ maxWidth: maxW }}>
        {children}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] font-black uppercase tracking-[0.15em] text-white/55 mb-2">{title}</p>
      {children}
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="text-[10px] font-bold text-white/45 uppercase tracking-wider">{label}</p>
      <p className={`text-white/85 mt-0.5 ${mono ? 'font-mono text-[11px]' : 'text-xs'} break-all`}>{value}</p>
    </div>
  );
}

/* ===========================================================================
 * Wrapper com tabs — Guard ganhou subsecoes: Sessoes / Banidos / Blacklist.
 * O conteudo da tab "Sessoes" e o componente GuardSessionsTab acima (que era
 * o antigo GuardPage). Banidos e Blacklist sao componentes proprios.
 * =========================================================================== */
import GuardBlacklistPage from './GuardBlacklistPage';
import BannedsPage from './BannedsPage';
import GuardHistoryPage from './GuardHistoryPage';
import GuardCrashLogsPage from './GuardCrashLogsPage';
import { History, Bug, Smartphone } from 'lucide-react';

type GuardTab = 'sessions' | 'moveguard' | 'speedhack' | 'banneds' | 'history' | 'blacklist' | 'crashlogs';

export default function GuardPage() {
  const [params] = useSearchParams();
  // Deep-link: ?loc=dungeon abre Sessões já filtrado; ?tab=moveguard abre a aba.
  const loc = params.get('loc');
  const initialLoc: LocFilter =
    loc === 'dungeon' || loc === 'campo' || loc === 'guerra' ? loc : 'all';
  // ?tab=next = a antiga aba "Cabal Next", que virou Sessões
  const tabParam = params.get('tab');
  const [tab, setTab] = useState<GuardTab>(
    !tabParam || tabParam === 'next' ? 'sessions' : (tabParam as GuardTab));
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
          <Shield className="text-emerald-400" size={26} /> Guard
        </h1>
        <p className="text-sm text-white/65 mt-1">
          Anti-cheat — sessões, anti-macro, banidos e blacklist de DLLs/diretórios.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 mb-5 surface-card rounded-xl p-1 w-fit max-w-full">
        <TabBtn active={tab === 'sessions'}  onClick={() => setTab('sessions')}>
          <Activity size={14} /> Sessões
        </TabBtn>
        <TabBtn active={tab === 'moveguard'} onClick={() => setTab('moveguard')}>
          <Gauge size={14} /> Anti-Macro
        </TabBtn>
        <TabBtn active={tab === 'speedhack'} onClick={() => setTab('speedhack')}>
          <Zap size={14} /> Speed-Hack
        </TabBtn>
        <TabBtn active={tab === 'banneds'}   onClick={() => setTab('banneds')}>
          <Ban size={14} /> Banidos
        </TabBtn>
        <TabBtn active={tab === 'history'}   onClick={() => setTab('history')}>
          <History size={14} /> Histórico
        </TabBtn>
        <TabBtn active={tab === 'blacklist'} onClick={() => setTab('blacklist')}>
          <AlertTriangle size={14} /> Blacklist
        </TabBtn>
        <TabBtn active={tab === 'crashlogs'} onClick={() => setTab('crashlogs')}>
          <Bug size={14} /> Crash Logs
        </TabBtn>
      </div>

      {tab === 'sessions'  && <GuardSessionsTab initialLoc={initialLoc} />}
      {tab === 'moveguard' && <GuardMoveguardTab />}
      {tab === 'speedhack' && <GuardSpeedHackTab />}
      {tab === 'banneds'   && <BannedsPage />}
      {tab === 'history'   && <GuardHistoryPage />}
      {tab === 'blacklist' && <GuardBlacklistPage />}
      {tab === 'crashlogs' && <GuardCrashLogsPage />}
    </div>
  );
}

/* ===========================================================================
 * Botão de filtro de localização (Campo / Dungeon / Guerra) na aba Sessões.
 * =========================================================================== */
function LocBtn({
  active, onClick, icon, label, count, color = '#00e1d7',
}: {
  active: boolean; onClick: () => void; icon: React.ReactNode;
  label: string; count: number; color?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer whitespace-nowrap border ${
        active ? 'text-white' : 'text-white/55 hover:text-white border-transparent hover:bg-white/[0.03]'
      }`}
      style={active ? { background: `${color}22`, borderColor: `${color}55`, color } : undefined}
    >
      {icon} {label}
      <span className={`ml-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-mono ${
        active ? 'bg-black/25' : 'bg-white/[0.06] text-white/50'
      }`}>{count}</span>
    </button>
  );
}

/* ===========================================================================
 * Tabela compartilhada de HISTÓRICO de alertas (persistido em _39_notifications).
 * Mostra quando/char/detalhe — os números crus vêm no `message` já formatado.
 * Sobrevive a restart da API (o feed em memória zerava).
 * =========================================================================== */
function AlertHistoryTable({ items, kind, loading }:
  { items: GuardAlert[]; kind: 'speed' | 'macro'; loading: boolean }) {
  const fmt = (iso?: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR');
  };
  const Icon = kind === 'speed' ? Zap : Skull;
  if (items.length === 0) {
    return (
      <div className="surface-card rounded-xl p-8 text-center">
        <Icon size={28} className="text-white/20 mx-auto mb-3" />
        <p className="text-sm text-white/60 font-semibold">
          {loading ? 'Carregando…' : 'Nenhuma detecção registrada'}
        </p>
        <p className="text-xs text-white/40 mt-1 max-w-md mx-auto">
          {kind === 'speed'
            ? 'Deslocamento/seg sustentado acima do teto legítimo (speed-hack, dash sem cooldown, teleport). Requer a .so nova empurrando o POSBATCH.'
            : 'Padrão de bot/macro (rota cíclica, farm confinado, trajetória repetida de DG) — do detector automático (moveguard) e do painel por-char.'}
        </p>
      </div>
    );
  }
  return (
    <div className="surface-card rounded-xl overflow-x-auto">
      <table className="w-full text-sm min-w-[640px]">
        <thead>
          <tr className="text-left text-[11px] uppercase text-white/40 border-b border-white/[0.06]">
            <th className="px-4 py-2.5 font-bold w-40">Quando</th>
            <th className="px-4 py-2.5 font-bold w-44">Char</th>
            <th className="px-4 py-2.5 font-bold">Detalhe</th>
          </tr>
        </thead>
        <tbody>
          {items.map((a) => (
            <tr key={a.notifId} className="border-b border-white/[0.04] hover:bg-white/[0.02] align-top">
              <td className="px-4 py-2.5 font-mono text-[11px] text-white/50 whitespace-nowrap">{fmt(a.ts)}</td>
              <td className="px-4 py-2.5">
                {a.charIdx ? (
                  <Link to={`/admin/character/${a.charIdx}`}
                        className="text-white font-bold hover:text-[#00e1d7] transition">
                    {a.name || `Char ${a.charIdx}`}
                  </Link>
                ) : <span className="text-white/50">—</span>}
                {a.charIdx != null && (
                  <span className="block text-[10px] text-white/35 font-mono">CharIdx {a.charIdx}</span>
                )}
              </td>
              <td className="px-4 py-2.5 text-xs text-white/75 leading-snug">{a.message}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ===========================================================================
 * Aba Anti-Macro — detector contínuo de bot/macro (moveguard, server-side).
 * Cards de status ao vivo (Hz/observados/calibração) + HISTÓRICO PERSISTIDO de
 * alertas de macro (de _39_notifications: moveguard automático + panel_guard_live
 * do painel por-char). OBSERVE — sem ban.
 * =========================================================================== */
function GuardMoveguardTab() {
  const [stats, setStats] = useState<MoveguardStats | null>(null);
  const [alerts, setAlerts] = useState<GuardAlert[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [s, a] = await Promise.all([
        getMoveguardStats().catch(() => null),
        listGuardAlerts('macro', 100),
      ]);
      setStats(s);
      setAlerts(a);
      setError(null);
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Erro ao carregar Anti-Macro');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const id = setInterval(() => fetchData(true), 8000);
    return () => clearInterval(id);
  }, [fetchData]);

  const streaming = (stats?.posbatchHz ?? 0) > 0;

  return (
    <div>
      {/* Cards de status do detector (ao vivo — em memória) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
        <div className="surface-card rounded-xl p-4">
          <div className="flex items-center gap-2 text-white/60 text-xs font-semibold mb-1">
            <Gauge size={14} /> Streaming (POSBATCH)
          </div>
          <span className={`text-2xl font-extrabold ${streaming ? 'text-emerald-400' : 'text-white/40'}`}>
            {stats ? `${stats.posbatchHz.toFixed(2)} Hz` : '—'}
          </span>
          <p className="text-[11px] text-white/45 mt-1">
            {streaming ? 'a .so está empurrando posições' : 'sem fluxo — .so nova ainda não ciclou nos canais'}
          </p>
        </div>
        <div className="surface-card rounded-xl p-4">
          <div className="flex items-center gap-2 text-white/60 text-xs font-semibold mb-1">
            <Users size={14} /> Observados
          </div>
          <span className="text-2xl font-extrabold text-white">{stats?.tracked ?? 0}</span>
          <p className="text-[11px] text-white/45 mt-1">chars com estado em memória</p>
        </div>
        <div className="surface-card rounded-xl p-4">
          <div className="flex items-center gap-2 text-white/60 text-xs font-semibold mb-1">
            <Activity size={14} /> Calibração
          </div>
          <span className={`text-2xl font-extrabold ${stats?.calibrate ? 'text-amber-400' : 'text-white/40'}`}>
            {stats?.calibrate ? 'LIGADA' : 'off'}
          </span>
          <p className="text-[11px] text-white/45 mt-1">
            {stats?.calibrate ? 'coletando percentis nos logs (OBSERVE)' : 'thresholds fixos'}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-white flex items-center gap-2">
          <Skull size={15} className="text-amber-400" /> Histórico de macro/bot ({alerts.length})
        </h3>
        <button
          onClick={() => fetchData()}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold
                     bg-[#00e1d7]/10 border border-[#00e1d7]/25 text-[#00e1d7]
                     hover:bg-[#00e1d7]/20 transition cursor-pointer disabled:opacity-50"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Atualizar
        </button>
      </div>

      {error && (
        <div className="surface-card rounded-xl p-4 mb-4 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <AlertHistoryTable items={alerts} kind="macro" loading={loading} />
    </div>
  );
}

/* ===========================================================================
 * Aba Speed-Hack — feed ao vivo das detecções de velocidade do MoveGuard.
 * A detecção já roda (kind='speed', teto MAX_UPS calibrado): deslocamento/seg
 * SUSTENTADO acima do teto legítimo = speed-hack / dash sem cooldown / teleport.
 * Aqui só mostramos o feed recente (memória, sem banco). OBSERVE — sem ban.
 * =========================================================================== */
function GuardSpeedHackTab() {
  const [items, setItems] = useState<GuardAlert[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      setItems(await listGuardAlerts('speed', 100));
      setError(null);
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Erro ao carregar detecções');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const id = setInterval(() => fetchData(true), 8000);
    return () => clearInterval(id);
  }, [fetchData]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <Zap size={15} className="text-amber-400" /> Detecções de velocidade
          </h3>
          <p className="text-[11px] text-white/45 mt-0.5">
            deslocamento/seg sustentado acima do teto legítimo · OBSERVE (sem ban) · histórico persistido
          </p>
        </div>
        <button
          onClick={() => fetchData()}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold
                     bg-[#00e1d7]/10 border border-[#00e1d7]/25 text-[#00e1d7]
                     hover:bg-[#00e1d7]/20 transition cursor-pointer disabled:opacity-50"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Atualizar
        </button>
      </div>

      {error && (
        <div className="surface-card rounded-xl p-4 mb-4 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <AlertHistoryTable items={items} kind="speed" loading={loading} />
    </div>
  );
}

function TabBtn({
  active, onClick, children,
}: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer whitespace-nowrap ${
        active
          ? 'bg-[#00e1d7]/15 text-[#00e1d7]'
          : 'text-white/55 hover:text-white hover:bg-white/[0.03]'
      }`}
    >
      {children}
    </button>
  );
}
